import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BusinessRoleView,
  BusinessRoleType,
  RoleAssignmentView,
  RoleAssignmentStatus,
  EmployeeSimpleView,
  getRoleAssignments,
  searchEmployeesForAssignment,
  assignRole,
  unassignRole,
  detectConflicts,
  RoleConflictView,
  StrengthAlignmentRowView,
  RoleFitCandidatesView,
  RoleFitCandidateView,
  getRoleFit,
  getRoleFitCandidates,
} from '../../../api/General/businessRoles';
import RoleConflictModal from './RoleConflictModal';
import RoleFitPanel, { POOR_FIT_THRESHOLD } from './RoleFitPanel';
import Button from '../../../components/ui/Button';
import { Spinner } from '../../../components/ui/Spinner';

interface RoleAssignmentPanelProps {
  role: BusinessRoleView;
  onAssignmentChanged: () => void;
}

const roleTypeStyles: Record<BusinessRoleType, string> = {
  [BusinessRoleType.Department]: 'bg-primary/10 text-primary',
  [BusinessRoleType.Project]: 'bg-purple-100 text-purple-700',
  [BusinessRoleType.Temporary]: 'bg-orange-100 text-orange-700',
};

const assignmentStatusStyles: Record<RoleAssignmentStatus, string> = {
  [RoleAssignmentStatus.Active]: 'bg-success/10 text-success',
  [RoleAssignmentStatus.Expired]: 'bg-muted text-muted-foreground',
  [RoleAssignmentStatus.Revoked]: 'bg-destructive/10 text-destructive',
};

const assignmentStatusLabels: Record<RoleAssignmentStatus, string> = {
  [RoleAssignmentStatus.Active]: 'Active',
  [RoleAssignmentStatus.Expired]: 'Expired',
  [RoleAssignmentStatus.Revoked]: 'Revoked',
};

export default function RoleAssignmentPanel({ role, onAssignmentChanged }: RoleAssignmentPanelProps) {
  const { t } = useTranslation();

  const [assignments, setAssignments] = useState<RoleAssignmentView[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAssignForm, setShowAssignForm] = useState(false);

  // Assign form state
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [employees, setEmployees] = useState<EmployeeSimpleView[]>([]);
  const [searchingEmployees, setSearchingEmployees] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeSimpleView | null>(null);
  const [effectiveDate, setEffectiveDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [expiryDate, setExpiryDate] = useState('');
  const [assignNotes, setAssignNotes] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Conflict modal
  const [conflicts, setConflicts] = useState<RoleConflictView[]>([]);
  const [showConflictModal, setShowConflictModal] = useState(false);

  // Fit advisor (Q3)
  const [fit, setFit] = useState<StrengthAlignmentRowView | null>(null);
  const [fitLoading, setFitLoading] = useState(false);
  const [fitCandidates, setFitCandidates] = useState<RoleFitCandidatesView | null>(null);
  const [warnFitPercent, setWarnFitPercent] = useState<number | undefined>(undefined);

  // Unassign
  const [unassigning, setUnassigning] = useState<number | null>(null);

  // Recommend employees (Q3-a)
  const [recommendOpen, setRecommendOpen] = useState(false);
  const [recommendLoading, setRecommendLoading] = useState(false);
  const [recommendations, setRecommendations] = useState<RoleFitCandidateView[]>([]);
  const [recommendDeptMissing, setRecommendDeptMissing] = useState(false);
  const [recommendError, setRecommendError] = useState(false);
  // Full pool size, set only when the server truncated a company-wide pool; 0 hides the notice.
  const [recommendPoolTotal, setRecommendPoolTotal] = useState(0);

  const loadAssignments = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getRoleAssignments(role.id);
      if (result.errors.length === 0) {
        setAssignments(result.payload.items);
      }
    } catch { /* silent */ } finally {
      setLoading(false);
    }
  }, [role.id]);

  useEffect(() => {
    loadAssignments();
  }, [loadAssignments]);

  // Close dropdown on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Debounced employee search
  // An empty search returns the first page of assignable employees, so the field can be browsed
  // rather than requiring the user to already know the name.
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    // Set before the debounce, not inside the request: the 300ms wait is part of what the user is
    // waiting through, and an empty box during it reads as "nobody matches".
    setSearchingEmployees(true);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const result = await searchEmployeesForAssignment(employeeSearch);
        if (result.errors.length === 0) {
          setEmployees(result.payload);
        }
      } catch { /* silent */ } finally {
        setSearchingEmployees(false);
      }
    }, 300);

    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, [employeeSearch]);

  const handleSelectEmployee = async (emp: EmployeeSimpleView) => {
    setSelectedEmployee(emp);
    setEmployeeSearch(emp.fullName);
    setShowDropdown(false);

    setFitLoading(true);
    setFit(null);
    try {
      const result = await getRoleFit(role.id, emp.id);
      if (result.errors.length === 0) {
        setFit(result.payload);
      }
    } catch { /* silent */ } finally {
      setFitLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedEmployee(null);
    setEmployeeSearch('');
    setEffectiveDate(new Date().toISOString().split('T')[0]);
    setExpiryDate('');
    setAssignNotes('');
    setShowAssignForm(false);
    setFit(null);
    setFitCandidates(null);
    setWarnFitPercent(undefined);
  };

  const handleAssign = async (forceAssign = false) => {
    if (!selectedEmployee) return;

    setAssigning(true);
    try {
      // First check for conflicts (unless force)
      if (!forceAssign) {
        const conflictResult = await detectConflicts(role.id, selectedEmployee.id);
        const hasConflicts = conflictResult.errors.length === 0 && conflictResult.payload.hasConflicts;
        // Advisory only: a poor fit routes through the same confirm as conflicts,
        // but is passed as fitPercent and never added to `conflicts`, so it cannot block.
        const poorFit = fit != null && fit.requirements.length > 0 && fit.coveragePercent < POOR_FIT_THRESHOLD;

        if (hasConflicts || poorFit) {
          setConflicts(hasConflicts ? conflictResult.payload.conflicts : []);

          if (poorFit && fit) {
            setWarnFitPercent(Math.round(fit.coveragePercent));
            try {
              const candidatesResult = await getRoleFitCandidates(role.id);
              if (candidatesResult.errors.length === 0) {
                setFitCandidates(candidatesResult.payload);
              }
            } catch { /* silent */ }
          } else {
            setWarnFitPercent(undefined);
          }

          setShowConflictModal(true);
          setAssigning(false);
          return;
        }
      }

      const result = await assignRole({
        businessRoleId: role.id,
        employeeId: selectedEmployee.id,
        effectiveDate,
        expiryDate: expiryDate || undefined,
        notes: assignNotes || undefined,
        forceAssign,
      });

      if (result.errors.length === 0) {
        resetForm();
        loadAssignments();
        onAssignmentChanged();
      }
    } catch { /* silent */ } finally {
      setAssigning(false);
    }
  };

  const handleConflictProceed = () => {
    setShowConflictModal(false);
    handleAssign(true);
  };

  const handleUnassign = async (assignmentId: number) => {
    if (!window.confirm(t('businessRoles.confirmUnassign'))) return;
    setUnassigning(assignmentId);
    try {
      const result = await unassignRole({ assignmentId });
      if (result.errors.length === 0) {
        loadAssignments();
        onAssignmentChanged();
      }
    } catch { /* silent */ } finally {
      setUnassigning(null);
    }
  };

  const handleRecommend = async () => {
    setRecommendOpen(true);
    setRecommendLoading(true);
    setRecommendError(false);
    try {
      const result = await getRoleFitCandidates(role.id);
      if (result.errors.length === 0) {
        const list = result.payload.candidates ?? [];
        // A missing department now means the pool is the whole company, so only an
        // empty pool still deserves the "set a department" hint.
        setRecommendDeptMissing(!result.payload.departmentName && result.payload.totalCandidates === 0);
        setRecommendPoolTotal(result.payload.isCapped ? result.payload.totalCandidates : 0);
        setRecommendations(list.slice(0, Math.max(0, seatsAvailable)));
      } else {
        setRecommendError(true);
        setRecommendDeptMissing(false);
        setRecommendPoolTotal(0);
        setRecommendations([]);
      }
    } catch {
      setRecommendError(true);
      setRecommendDeptMissing(false);
      setRecommendPoolTotal(0);
      setRecommendations([]);
    } finally {
      setRecommendLoading(false);
    }
  };

  const handleSelectRecommendation = (c: RoleFitCandidateView) => {
    handleSelectEmployee({ id: c.employeeId, fullName: c.employeeName || '', department: c.department });
    setShowAssignForm(true);
    setRecommendOpen(false);
  };

  const activeAssignments = assignments.filter(a => a.status === RoleAssignmentStatus.Active);
  const pastAssignments = assignments.filter(a => a.status !== RoleAssignmentStatus.Active);
  const seatsAvailable = role.numberOfSeats - activeAssignments.length;

  return (
    <div className="mt-6 pt-6 border-t border-border">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-foreground">{t('businessRoles.assignments')}</h3>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={seatsAvailable <= 0}
            onClick={handleRecommend}
            iconName="Sparkles"
            data-testid="recommend-employees-btn"
          >
            {t('teamRole.recommendEmployees')}
          </Button>
          {seatsAvailable > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAssignForm(!showAssignForm)}
              iconName="UserPlus"
            >
              {t('businessRoles.assignEmployee')}
            </Button>
          )}
        </div>
      </div>

      {/* Recommended shortlist (Q3-a) */}
      {recommendOpen && (
        <div className="bg-muted rounded-lg p-4 mb-4 border border-border">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-medium text-muted-foreground uppercase">{t('teamRole.recommendEmployees')}</p>
            <Button variant="ghost" size="sm" onClick={() => setRecommendOpen(false)}>
              {t('common.close')}
            </Button>
          </div>

          {recommendLoading ? (
            <div className="flex items-center justify-center py-6">
              <Spinner size="sm" />
            </div>
          ) : recommendError ? (
            <div className="flex items-center justify-between gap-2 py-1">
              <p className="text-sm text-muted-foreground">{t('teamRole.recommendError')}</p>
              <Button variant="outline" size="sm" onClick={handleRecommend}>
                {t('common.retry')}
              </Button>
            </div>
          ) : recommendDeptMissing ? (
            <p className="text-sm text-muted-foreground py-1">{t('teamRole.recommendNoDepartment')}</p>
          ) : recommendations.length === 0 ? (
            <p className="text-sm text-muted-foreground py-1">{t('teamRole.recommendNoMembers')}</p>
          ) : (
            <div className="space-y-2">
              {recommendPoolTotal > 0 && (
                <p className="text-xs text-muted-foreground" data-testid="fit-candidates-capped">
                  {t('businessRoles.candidatesCapped', { shown: recommendations.length, total: recommendPoolTotal })}
                </p>
              )}
              <p className="text-xs text-muted-foreground mb-1">
                {t('teamRole.recommendCountHint', { count: recommendations.length })}
              </p>
              {recommendations.map((c) => (
                <div
                  key={c.employeeId}
                  data-testid={`recommend-candidate-${c.employeeId}`}
                  className="flex items-center justify-between gap-2 p-3 rounded-lg border border-border bg-card"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-medium text-primary">
                        {(c.employeeName || '?').charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{c.employeeName || c.employeeId}</p>
                      <div className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
                        {c.department && <span className="truncate">{c.department}</span>}
                        <span>
                          {t('roleFit.candidateSummary', {
                            percent: c.coveragePercent,
                            strong: c.strongCount,
                            developing: c.developingCount,
                            missing: c.missingCount,
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => handleSelectRecommendation(c)}>
                    {t('teamRole.recommendSelect')}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Assign Form */}
      {showAssignForm && (
        <div className="bg-muted rounded-lg p-4 mb-4 border border-border">
          <div className="space-y-3">
            {/* Employee search */}
            <div className="relative" ref={dropdownRef}>
              <label className="block text-xs font-medium text-foreground mb-1">{t('businessRoles.selectEmployee')}</label>
              <input
                type="text"
                value={employeeSearch}
                onChange={(e) => {
                  setEmployeeSearch(e.target.value);
                  if (selectedEmployee && e.target.value !== selectedEmployee.fullName) {
                    setSelectedEmployee(null);
                    setFit(null);
                  }
                }}
                onFocus={() => setShowDropdown(true)}
                placeholder={t('businessRoles.searchEmployee')}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                data-testid="assignment-employee-input"
              />
              {selectedEmployee && (
                <div className="absolute end-2 top-7 flex items-center">
                  <svg className="w-4 h-4 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}
              {/* Also open while a typed search has come back with nothing: the empty state lives in
                  here, and gating the panel on results alone was what made a no-match search look
                  like a broken field in the first place. */}
              {showDropdown
                && (employees.length > 0 || searchingEmployees || employeeSearch.trim().length > 0) && (
                <div className="absolute z-10 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {searchingEmployees && (
                    <p
                      className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground"
                      role="status"
                      data-testid="assignment-employee-searching"
                    >
                      <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                      </svg>
                      {t('businessRoles.employeeSearching')}
                    </p>
                  )}
                  {employees.map((emp) => (
                    <button
                      key={emp.id}
                      onClick={() => handleSelectEmployee(emp)}
                      className="w-full text-start px-3 py-2 hover:bg-muted flex items-center gap-2"
                    >
                      <div className="w-7 h-7 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-medium text-primary">
                          {emp.fullName.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm text-foreground truncate">{emp.fullName}</p>
                        {emp.department && (
                          <p className="text-xs text-muted-foreground">{emp.department}</p>
                        )}
                      </div>
                    </button>
                  ))}
                  {/* A search that matched nobody must say so rather than close the panel and leave
                      the user typing into what looks like a broken field. */}
                  {!searchingEmployees && employees.length === 0 && (
                    <p
                      className="px-3 py-2 text-xs text-muted-foreground"
                      data-testid="assignment-employee-empty"
                    >
                      {t('businessRoles.noEmployeeMatches')}
                    </p>
                  )}
                  {/* The server caps the page at 20, so browsing must not imply this is everyone. */}
                  {employees.length >= 20 && (
                    <p className="border-t border-border px-3 py-2 text-xs text-muted-foreground">
                      {t('businessRoles.employeeListCapped')}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Role fit advisor */}
            {selectedEmployee && (
              <RoleFitPanel
                role={role}
                fit={fit}
                loading={fitLoading}
                employeeId={selectedEmployee.id}
                onFitRefreshed={setFit}
              />
            )}

            {/* Date fields */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">{t('businessRoles.effectiveDate')}</label>
                <input
                  type="date"
                  value={effectiveDate}
                  onChange={(e) => setEffectiveDate(e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">
                  {t('businessRoles.expiryDate')}
                  <span className="text-muted-foreground font-normal ms-1">({t('common.optional')})</span>
                </label>
                <input
                  type="date"
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(e.target.value)}
                  min={effectiveDate}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                />
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">
                {t('businessRoles.assignmentNotes')}
                <span className="text-muted-foreground font-normal ms-1">({t('common.optional')})</span>
              </label>
              <input
                type="text"
                value={assignNotes}
                onChange={(e) => setAssignNotes(e.target.value)}
                placeholder={t('businessRoles.assignmentNotesPlaceholder')}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" size="sm" onClick={resetForm}>
                {t('common.cancel')}
              </Button>
              <Button
                size="sm"
                onClick={() => handleAssign(false)}
                disabled={!selectedEmployee || assigning}
                loading={assigning}
              >
                {t('businessRoles.assign')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Seat indicator */}
      <div className="flex items-center gap-2 mb-3">
        <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${
              seatsAvailable <= 0 ? 'bg-destructive' : seatsAvailable <= 1 ? 'bg-warning' : 'bg-success'
            }`}
            style={{ width: `${Math.min(100, (activeAssignments.length / role.numberOfSeats) * 100)}%` }}
          />
        </div>
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {activeAssignments.length}/{role.numberOfSeats} {t('businessRoles.seatsUsed')}
        </span>
      </div>

      {/* Loading */}
      {loading ? (
        <div className="flex items-center justify-center py-6">
          <Spinner size="sm" />
        </div>
      ) : assignments.length === 0 ? (
        <div className="text-center py-6">
          <p className="text-sm text-muted-foreground">{t('businessRoles.noAssignments')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Active assignments */}
          {activeAssignments.length > 0 && (
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase mb-2">{t('businessRoles.currentAssignees')}</p>
              <div className="space-y-2">
                {activeAssignments.map((a) => {
                  const isExpiringSoon = a.expiryDate && new Date(a.expiryDate) <= new Date(Date.now() + 14 * 24 * 60 * 60 * 1000);
                  return (
                    <div key={a.id} className={`flex items-center justify-between p-3 rounded-lg border ${
                      isExpiringSoon ? 'border-warning/20 bg-warning/5' : 'border-border bg-card'
                    }`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-medium text-primary">
                            {(a.employeeName || '?').charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">{a.employeeName || a.employeeId}</p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span>{new Date(a.effectiveDate).toLocaleDateString()}</span>
                            {a.expiryDate && (
                              <>
                                <span>-</span>
                                <span className={isExpiringSoon ? 'text-warning font-medium' : ''}>
                                  {new Date(a.expiryDate).toLocaleDateString()}
                                  {isExpiringSoon && ` (${t('businessRoles.expiringSoon')})`}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive hover:bg-destructive/5"
                        onClick={() => handleUnassign(a.id)}
                        disabled={unassigning === a.id}
                      >
                        {unassigning === a.id ? '...' : t('businessRoles.unassign')}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Past assignments */}
          {pastAssignments.length > 0 && (
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase mb-2">{t('businessRoles.pastAssignments')}</p>
              <div className="space-y-1.5">
                {pastAssignments.map((a) => (
                  <div key={a.id} className="flex items-center justify-between p-2.5 rounded-lg border border-border bg-muted">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="w-7 h-7 bg-muted rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-medium text-muted-foreground">
                          {(a.employeeName || '?').charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-foreground truncate">{a.employeeName || a.employeeId}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(a.effectiveDate).toLocaleDateString()}
                          {a.expiryDate && ` - ${new Date(a.expiryDate).toLocaleDateString()}`}
                        </p>
                      </div>
                    </div>
                    <span className={`inline-flex px-1.5 py-0.5 rounded text-xs font-medium ${assignmentStatusStyles[a.status]}`}>
                      {t(`businessRoles.assignment${assignmentStatusLabels[a.status]}`)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Conflict Modal */}
      <RoleConflictModal
        open={showConflictModal}
        conflicts={conflicts}
        onProceed={handleConflictProceed}
        onCancel={() => setShowConflictModal(false)}
        fitCandidates={fitCandidates}
        fitPercent={warnFitPercent}
      />
    </div>
  );
}
